/**
 * PlanUpgradeModal — reusable modal triggered from any Pro-gated feature.
 * Ticket #340
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, Button, List, ListItem,
  ListItemIcon, ListItemText, IconButton, Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';

interface PlanUpgradeModalProps {
  open: boolean;
  onClose: () => void;
  featureName?: string; // e.g. "Trip Companions"
}

const PRO_BENEFITS = [
  '15 GB photo storage',
  'Trip companions (up to 6)',
  'BFFs / Travel Partners visibility',
  'Expiration control & notifications',
  'Photo approval workflow',
];

const PlanUpgradeModal: React.FC<PlanUpgradeModalProps> = ({ open, onClose, featureName }) => {
  const navigate = useNavigate();

  const handleViewPlans = () => {
    onClose();
    navigate('/plans');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px', p: 0, overflow: 'visible' } }}
    >
      {/* Teal header */}
      <Box sx={{
        bgcolor: '#2D6E6A',
        borderRadius: '20px 20px 0 0',
        px: 3, pt: 3, pb: 2.5,
        position: 'relative',
      }}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12, color: 'white', opacity: 0.8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.15)',
            borderRadius: '50%', width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <LockOutlinedIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
          </Box>
          <Box>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', fontFamily: 'Poppins, sans-serif' }}>
              Pro Feature
            </Typography>
            {featureName && (
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                {featureName} requires Pro
              </Typography>
            )}
          </Box>
        </Box>
        <Chip
          label="Free for a limited time"
          size="small"
          icon={<StarIcon sx={{ fontSize: '0.9rem !important', color: '#FFB347 !important' }} />}
          sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, fontSize: '0.75rem' }}
        />
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6 }}>
          Upgrade to Pro — free for early access users — and unlock:
        </Typography>
        <List dense disablePadding>
          {PRO_BENEFITS.map((benefit, i) => (
            <ListItem key={i} disablePadding sx={{ py: 0.4 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckCircleIcon sx={{ fontSize: 17, color: '#2D6E6A' }} />
              </ListItemIcon>
              <ListItemText
                primary={benefit}
                primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.875rem' } }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleViewPlans}
          sx={{
            bgcolor: '#2D6E6A', color: 'white', borderRadius: '24px',
            textTransform: 'none', fontWeight: 700, py: 1.25,
            '&:hover': { bgcolor: '#1F4E4A' },
          }}
        >
          Join Early Access — It's Free
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={onClose}
          sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500 }}
        >
          Maybe later
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanUpgradeModal;
